import React, { createContext, useContext } from "react";

import DistanceFilterType from "../types/DistanceFilterType";
import LocationData from "../types/LocationData";
import SpecialtyFilterType from "../types/SpecialtyFilterType";

interface Props {
    children: JSX.Element;
    google: unknown;
}

export const LocatorContext = createContext(null);

export const LocatorContextProvider = ({ google, children }: Props): JSX.Element => {
    const [activeHit, setActiveHit] = React.useState(null);
    const [mapFullScreen, setMapFullScreen] = React.useState(false);
    const [infoPanelVisible, setInfoPanelVisible] = React.useState(false);
    const [specialtyFilter, setSpecialtyFilter] = React.useState([] as SpecialtyFilterType[]);
    const [distanceFilter, setDistanceFilter] = React.useState(null as DistanceFilterType);
    const [trackingName, setTrackingName] = React.useState(null);
    const [noResults, setNoResults] = React.useState(false);

    const [googleObject] = React.useState(google);

    return (
        <LocatorContext.Provider
            value={{
                activeHit: activeHit,
                setActiveHit: setActiveHit,
                mapFullScreen: mapFullScreen,
                setMapFullScreen: setMapFullScreen,
                infoPanelVisible,
                setInfoPanelVisible,
                specialtyFilter,
                setSpecialtyFilter,
                distanceFilter,
                setDistanceFilter,
                google: googleObject,
                trackingName,
                setTrackingName,
                noResults,
                setNoResults,
            }}
        >
            {children}
        </LocatorContext.Provider>
    );
};

export const useLocatorContext = (): {
    activeHit?: LocationData;
    mapFullScreen?: boolean;
    infoPanelVisible?: boolean;
    specialtyFilter?: SpecialtyFilterType[];
    distanceFilter?: DistanceFilterType;
    setActiveHit?: (data: LocationData) => null;
    setMapFullScreen?: (value: boolean) => null;
    setInfoPanelVisible?: (value: boolean) => null;
    setSpecialtyFilter?: (value: SpecialtyFilterType[]) => void;
    setDistanceFilter?: (value: DistanceFilterType) => void;
    google?: unknown;
    trackingName?: string;
    setTrackingName?: (value: string) => null;
    noResults?: boolean;
    setNoResults?: (value: boolean) => null;
} => {
    const {
        activeHit = false,
        setActiveHit = null,
        mapFullScreen = false,
        setMapFullScreen = null,
        infoPanelVisible = false,
        setInfoPanelVisible = null,
        specialtyFilter = null,
        setSpecialtyFilter = null,
        distanceFilter = null,
        setDistanceFilter = null,
        google = null,
        trackingName = null,
        setTrackingName = null,
        noResults = false,
        setNoResults = null,
    } = useContext(LocatorContext);
    return {
        activeHit,
        setActiveHit,
        mapFullScreen,
        setMapFullScreen,
        infoPanelVisible,
        setInfoPanelVisible,
        specialtyFilter,
        setSpecialtyFilter,
        distanceFilter,
        setDistanceFilter,
        google,
        trackingName,
        setTrackingName,
        noResults,
        setNoResults,
    };
};

export default { LocatorContextProvider, LocatorContext, useLocatorContext };
